/**
 * FormulaEditor.tsx — list a calc's formulas; edit outputKey + expr.
 *
 * Each formula's expr is a config-eval Expr tree (JSON). A chip palette derived
 * from the calc's inputs + already-declared outputs inserts a ready-made ref
 * node — `{ "op": "input", "key": "<k>" }` or `{ "op": "ref", "key": "<out>" }`
 * — at the cursor of the JSON editor. This mirrors S4's buildChipPalette token
 * list (inputs + outputs), reused here for the formula surface.
 */

import { useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { CalcSpec, SpecFormula, StudioInputField } from '../../../api/calculatorBuilder';
import { StudioPanel, field, btnAccent, label, btnGhostDanger } from './ui';

interface Props {
  spec: CalcSpec;
  inputs: StudioInputField[];
  onChange: (formulas: SpecFormula[]) => void;
}

export default function FormulaEditor({ spec, inputs, onChange }: Props) {
  const formulas = spec.formulas ?? [];
  const textareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});

  const patch = (i: number, p: Partial<SpecFormula>) => onChange(formulas.map((f, idx) => (idx === i ? { ...f, ...p } : f)));
  const add = () => onChange([...formulas, { outputKey: `output_${formulas.length + 1}`, expr: { op: 'const', value: 0 } }]);
  const remove = (i: number) => onChange(formulas.filter((_, idx) => idx !== i));

  // Chip palette: inputs + prior outputs (so a formula can reference an earlier output).
  const inputChips = (inputs.length ? inputs : spec.inputs ?? []).map((f) => ({ token: `input.${f.key}`, label: f.label || f.key, node: { op: 'input', key: f.key } }));
  const outputChips = formulas.map((f) => ({ token: `output.${f.outputKey}`, label: f.outputKey, node: { op: 'ref', key: f.outputKey } }));

  const insertRef = (i: number, node: Record<string, unknown>) => {
    const ta = textareaRefs.current[i];
    const snippet = JSON.stringify(node);
    const current = JSON.stringify(formulas[i].expr, null, 2);
    if (ta && document.activeElement === ta) {
      const start = ta.selectionStart ?? current.length;
      const end = ta.selectionEnd ?? current.length;
      const next = current.slice(0, start) + snippet + current.slice(end);
      try {
        patch(i, { expr: JSON.parse(next) });
      } catch {
        // leave as-is if the manual splice broke JSON; the admin can fix in the textarea
        ta.value = next;
      }
    } else {
      // no cursor: default to inserting as the whole expr
      patch(i, { expr: node });
    }
  };

  return (
    <StudioPanel
      title="Formulas"
      subtitle="Each output is a config-eval expression (JSON). Use the chip menus to drop input/output references."
      right={
        <button type="button" style={btnAccent} onClick={add}>
          <Plus size={14} /> Add formula
        </button>
      }
    >
      {formulas.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-3, #6b7280)' }}>No formulas yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {formulas.map((f, i) => (
          <div key={i} style={{ border: '1px solid var(--line, #e5e7eb)', borderRadius: 9, padding: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px' }}>
                <span style={label}>Output key</span>
                <input
                  style={{ ...field, width: '100%', fontFamily: 'ui-monospace, monospace' }}
                  value={f.outputKey}
                  onChange={(e) => patch(i, { outputKey: e.target.value.trim() })}
                />
              </div>
              <select
                style={{ ...field }}
                value=""
                onChange={(e) => {
                  const chip = inputChips.find((c) => c.token === e.target.value);
                  if (chip) insertRef(i, chip.node);
                  e.target.value = '';
                }}
              >
                <option value="">+ input ref</option>
                {inputChips.map((c) => (
                  <option key={c.token} value={c.token}>{c.label}</option>
                ))}
              </select>
              <select
                style={{ ...field }}
                value=""
                onChange={(e) => {
                  const chip = outputChips.find((c) => c.token === e.target.value);
                  if (chip) insertRef(i, chip.node);
                  e.target.value = '';
                }}
              >
                <option value="">+ output ref</option>
                {outputChips.filter((c) => c.label !== f.outputKey).map((c) => (
                  <option key={c.token} value={c.token}>{c.label}</option>
                ))}
              </select>
              <button type="button" style={btnGhostDanger} onClick={() => remove(i)}>
                <Trash2 size={14} /> Remove
              </button>
            </div>
            <div style={{ marginTop: 10 }}>
              <span style={label}>Expression (config-eval JSON)</span>
              <textarea
                ref={(el) => { textareaRefs.current[i] = el; }}
                style={{
                  ...field, width: '100%', height: 120, padding: 10, resize: 'vertical',
                  fontFamily: 'ui-monospace, monospace', fontSize: 12,
                }}
                value={JSON.stringify(f.expr, null, 2)}
                onChange={(e) => {
                  try {
                    patch(i, { expr: JSON.parse(e.target.value) });
                  } catch {
                    // hold raw text in the DOM until it parses; don't clobber state with invalid JSON
                    if (textareaRefs.current[i]) textareaRefs.current[i]!.value = e.target.value;
                  }
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--ink-3, #6b7280)' }}>
        Ops: <code>const, input, ref, +, -, *, /, max, min, ceil, floor, round, if, lookup, stepUp</code>
      </div>
    </StudioPanel>
  );
}
